
import { defineComponent } from "vue";
import { DRAW_MODE, MapDrawMenuItem, MapDrawSelection } from "@/types";
import FabMenuItem from "@/components/FabMenuItem.vue";
import {
  distanceRounder,
  lengthUnit,
  lengthUnits,
  metricConvertor,
} from "@/util/helpers";
import { BDropdown, BFormInput } from "bootstrap-vue";
import { get } from "lodash-es";
import {
  useLayout,
  useGammaData,
  useMap,
  useOrgSettings,
  useLocation,
} from "@/services";
import { mapState } from "pinia";
import useRiskGeometry from "@/composeable/risk-geometry";

export default defineComponent({
  name: "AccumulationsMenu",
  components: {
    FabMenuItem,
  },
  data: () => ({
    selectedMode: "draw_circle" as DRAW_MODE,
    isEditingMenu: false,
    selectedUnit: "m" as lengthUnit,
    units: [...lengthUnits.keys()],
  }),
  computed: {
    ...mapState(useMap, ["dataBoundary"]),
    ...mapState(useOrgSettings, ["showBottomAccumulation"]),
    isEditingLayer() {
      return useMap().isEditingLayer;
    },
    editorMenu(): MapDrawMenuItem[] {
      switch (true) {
        case this.editingMode.mode === "direct_select":
          return (
            useMap().isEditingCircle
              ? [
                  {
                    type: "input",
                  },
                ]
              : []
          ).map((o) => this.parseOption(o));
        case this.editingMode.mode === "simple_select" && this.hasBoundary:
          return [
            {
              mode: "direct_select",
              icon: "pencil-square",
              iconType: "bi",
              title: "Modify zone",
            },
            {
              icon: "x",
              iconType: "bi",
              action: this.clearMap,
              variant: "outline-danger",
              title: "Delete zone",
            },
          ].map((o) => this.parseOption(o));
        default:
          return [
            {
              mode: "draw_polygon",
              icon: "s-polygon",
              title: "Polygonal selection",
            },
            {
              mode: "draw_circle",
              icon: "s-circle",
              title: "Circular selection",
            },
            {
              type: "input",
            },
          ].map((o) => this.parseOption(o));
      }
    },
    displayedRadius: {
      get(): number {
        return distanceRounder(
          useMap().circleRadius * metricConvertor(this.selectedUnit),
          this.selectedUnit
        );
      },
      set(value: number) {
        useMap().circleRadius = distanceRounder(
          value / metricConvertor(this.selectedUnit),
          "m"
        );
        this.$bus.$emit("updateCircleRadius", useMap().circleRadius / 1000);
      },
    },
    hasDataSources(): boolean {
      return useGammaData().hasDataSources;
    },
    isMenuOpen: {
      get(): boolean {
        return useLayout().isDrawToolOpen;
      },
      set(val: boolean) {
        useLayout().isDrawToolOpen = val;
      },
    },
    editingMode: {
      get(): Partial<MapDrawSelection> {
        return useMap().editingMode;
      },
      set(val: Partial<MapDrawSelection>) {
        useMap().setEditingMode(val, false);
      },
    },
    hasBoundary(): boolean {
      return !!(this.dataBoundary || this.inDirectSelectMode);
    },
    inDirectSelectMode(): boolean {
      return this.editingMode.mode === "direct_select";
    },
    shouldRenderRadiusControl(): boolean {
      return (
        this.editingMode.mode === "draw_circle" || useMap().isEditingCircle
      );
    },
    showAccumulationMenu(): boolean {
      return (
        this.hasDataSources &&
        !this.isEditingLayer &&
        this.showBottomAccumulation
      );
    },
  },
  watch: {
    "editingMode.mode": {
      handler(val: DRAW_MODE) {
        if (val === "draw_circle" && !this.isMenuOpen) {
          this.isMenuOpen = true;
        }
      },
    },
  },
  methods: {
    selectUnit(unit: lengthUnit) {
      this.selectedUnit = unit;
      const unitsDd = get(this, ["$refs", "unitsDd", 0]) as
        | BDropdown
        | undefined;
      const radiusInput = get(this, ["$refs", "radiusInput", 0]) as
        | BFormInput
        | undefined;
      if (unitsDd instanceof BDropdown) {
        unitsDd.hide();
      }
      if (radiusInput?.$el instanceof HTMLInputElement) {
        radiusInput.$el.focus();
      }
    },
    parseOption(o: MapDrawMenuItem): MapDrawMenuItem {
      return o.icon
        ? {
            ...o,
            src:
              o.iconType === "bi"
                ? ""
                : require(`../../assets/icons/${o.icon}.svg`),
            action: o.action || this.updateEditingMode,
            variant: o.variant || "light",
          }
        : o;
    },
    toggleMenu() {
      const map = useMap();
      if (!this.isMenuOpen && map.isDrawingRiskGeom) {
        map.mapDraw.deleteAll();
        useRiskGeometry().endDraw();
        useLocation().updateLocation(null);
      }
      this.isMenuOpen = !this.isMenuOpen;
      this.$nextTick(() => {
        if (!this.isMenuOpen) {
          this.editingMode = { mode: "simple_select" };
        } else {
          if (!this.hasBoundary) {
            this.editingMode = { mode: this.selectedMode };
          }
        }
      });
    },
    updateEditingMode(option: Partial<MapDrawSelection>): void {
      if (option.mode === this.editingMode.mode) {
        return;
      }
      if (option.icon === "x") {
        this.clearMap();
      } else if (option.mode === "direct_select") {
        this.editingMode = { mode: "direct_select" };
        this.isEditingMenu = true;
      } else {
        this.editingMode = option;
        if (option.mode) {
          this.selectedMode = option.mode;
        }
        this.isEditingMenu = false;
      }
    },
    isOptionSelected(option: Partial<MapDrawSelection>) {
      return this.editingMode.mode === option.mode ? "info" : "light";
    },
    clearMap() {
      useMap().updateBoundaryPolygon(null);

      this.editingMode = { mode: "simple_select" };
      this.isMenuOpen = false;
      useGammaData().fetchAccumulations();
    },
  },
});
